import { exitSearchTitleNext } from './SearchBox.locstring.json';
import initializeScenarioStore from '../actions/initializeScenarioStore';
import onSearchBoxBlurred from '../actions/onSearchBoxBlurred';
import onSearchBoxFocused from '../actions/onSearchBoxFocused';
import getScenarioStore from '../store/getScenarioStore';
import getSearchBoxMaxWidth from '../utils/getSearchBoxMaxWidth';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { elementContains } from 'office-ui-fabric-react/lib/Utilities';
import { ControlIcons } from 'owa-control-icons';
import loc from 'owa-localize';
import { SearchPlaceholder } from 'owa-locstrings/lib/strings/searchplaceholder.locstring.json';
import { SEARCHBOX_ANCHOR_ID, SEARCHBOX_ID, SearchScenarioId } from 'owa-search-next-common';
import * as React from 'react';
import {
    exitSearch,
    lazyGetSearchStore as lazyGetLazySearchStore,
    lazyHasSuggestionPills,
    lazyIsSearchBoxEmpty,
    lazyIsSearchSessionActive,
    SearchBoxPillWell,
    SearchInput,
    ISearchInput,
    startSearch,
    SuggestionsCallout,
    onRemoveSuggestionPill,
    onRemoveGuidedFormulationPill,
} from 'owa-search-next-lazy';

/* tslint:disable:jsx-no-lambda WI:47653 */
const classNames = require('classnames');
const styles = require('./SearchBox.scss');
const LPC_CARD_ID = 'LPCPseudoTabbableElement';

export interface SearchBoxProps {
    customCollapsedMaxWidth?: number; // The max width when the control is collapsed
    isDisabled?: boolean; // Boolean indicating if search box is disabled (default is false)
    isUsing3S?: boolean; // Is using 3S for search suggestions (for instrumentation)
    renderFolderScope?: () => JSX.Element; // Render prop for folder scope and menu
    renderRefiners?: () => JSX.Element; // Render prop for search refiners button and menu
    scenarioId: SearchScenarioId; // ID representing the search scenario (unique per scenario)
    searchPlaceHolderText?: string; // Placeholder string to be displayed in search input
}

@observer
export default class SearchBox extends React.Component<SearchBoxProps, {}> {
    private searchBoxContainer: HTMLDivElement;
    private searchInput: React.RefObject<ISearchInput> = React.createRef();

    constructor(props: SearchBoxProps) {
        super(props);

        const { scenarioId, isUsing3S = true } = props;
        initializeScenarioStore(scenarioId, isUsing3S);
    }

    /**
     * This computed represents if suggestions callout is shown.
     */
    @computed
    get isSuggestionsCalloutVisible(): boolean {
        const getLazySearchStore = lazyGetLazySearchStore.tryImportForRender();
        if (getLazySearchStore) {
            const { isSuggestionsDropdownVisible, currentSuggestions } = getLazySearchStore(
                this.props.scenarioId
            );

            return (
                currentSuggestions &&
                currentSuggestions.Suggestions &&
                currentSuggestions.Suggestions.length > 0 &&
                isSuggestionsDropdownVisible
            );
        }

        return false;
    }

    @computed
    get searchGlyphClasses() {
        return classNames(styles.searchIcon, this.props.isDisabled && styles.isDisabled);
    }

    @computed
    get isSearchSessionActive(): boolean {
        const isSearchSessionActiveSelector = lazyIsSearchSessionActive.tryImportForRender();

        if (isSearchSessionActiveSelector) {
            return isSearchSessionActiveSelector(this.props.scenarioId);
        } else {
            return false;
        }
    }

    @computed
    get hasSuggestionPills(): boolean {
        const hasSuggestionPillsSelector = lazyHasSuggestionPills.tryImportForRender();

        if (hasSuggestionPillsSelector) {
            return hasSuggestionPillsSelector(this.props.scenarioId);
        } else {
            return false;
        }
    }

    render() {
        const searchBoxContainerStyle = {
            maxWidth: `${getSearchBoxMaxWidth(
                this.isSearchSessionActive,
                this.props.customCollapsedMaxWidth
            )}px`,
            width: '100%',
        };

        const searchBoxContainerClassNames = classNames(
            styles.searchBoxContainer,
            this.isSearchSessionActive && styles.searchBoxContainerInSearch
        );

        let searchBoxStyle = styles.searchBox;

        const searchBoxClassNames = classNames(
            searchBoxStyle,
            this.props.isDisabled && styles.isDisabled
        );

        const suggestionsComponent = this.isSuggestionsCalloutVisible ? (
            <SuggestionsCallout scenarioId={this.props.scenarioId} />
        ) : null;

        return (
            /**
             * Outer div is necessary to make this component have a single
             * root element.
             */
            <div style={searchBoxContainerStyle}>
                <div
                    role={'search'}
                    tabIndex={-1}
                    className={searchBoxContainerClassNames}
                    ref={ref => (this.searchBoxContainer = ref)}
                    aria-expanded={this.isSuggestionsCalloutVisible}
                    onBlur={this.onSearchBoxBlur}>
                    {/* Folder scope */}
                    {this.isSearchSessionActive && this.props.renderFolderScope && (
                        /**
                         * This wrapper div with tabindex of -1 is required
                         * for focus events to have relatedTarget property
                         * be non-null (because element receiving focus
                         * needs to be able to receive focus, which divs
                         * cannot unless given a tabindex value) in Safari.
                         */
                        <div tabIndex={-1}>{this.props.renderFolderScope()}</div>
                    )}
                    {/* Search box */}
                    <div id={SEARCHBOX_ID} className={searchBoxClassNames}>
                        {/* Suggestions UI */}
                        {suggestionsComponent}

                        {/* Left side (back button or search icon) */}
                        {this.isSearchSessionActive ? (
                            <IconButton
                                className={classNames(styles.backButton, 'flipForRtl')}
                                iconProps={{ iconName: ControlIcons.Back }}
                                onClick={() => {
                                    this.onExit('SearchBoxBackArrow');
                                }}
                                ariaLabel={loc(exitSearchTitleNext)}
                            />
                        ) : (
                            <div
                                aria-hidden={true}
                                className={classNames(
                                    styles.searchIcon,
                                    this.props.isDisabled && styles.isDisabled
                                )}
                                onClick={this.onSearchBoxClick}>
                                <Icon iconName={ControlIcons.Search} />
                            </div>
                        )}

                        {/* Search input (pills and input) */}
                        <div
                            className={styles.searchQueryContainer}
                            onClick={this.onSearchBoxClick}>
                            {this.hasSuggestionPills && (
                                <SearchBoxPillWell
                                    focusSearchInput={this.focusSearchInput}
                                    onRemovePill={this.onRemovePill}
                                    onRemoveSegment={this.onRemoveSegment}
                                    scenarioId={this.props.scenarioId}
                                />
                            )}
                            <SearchInput
                                ref={this.searchInput}
                                searchPlaceHolderText={this.props.searchPlaceHolderText}
                                isDisabled={this.props.isDisabled}
                                scenarioId={this.props.scenarioId}
                                isSuggestionsCalloutVisible={this.isSuggestionsCalloutVisible}
                                isUsing3S={getScenarioStore(this.props.scenarioId).isUsing3S}
                            />
                        </div>

                        {/* Search refiners */}
                        {this.isSearchSessionActive && this.props.renderRefiners && (
                            /**
                             * This wrapper div with tabindex of -1 is required
                             * for focus events to have relatedTarget property
                             * be non-null (because element receiving focus
                             * needs to be able to receive focus, which divs
                             * cannot unless given a tabindex value) in Safari.
                             */
                            <div tabIndex={-1}>{this.props.renderRefiners()}</div>
                        )}
                    </div>

                    {/* Search button */}
                    {this.isSearchSessionActive && (
                        <IconButton
                            className={styles.searchButton}
                            iconProps={{
                                iconName: ControlIcons.Search,
                            }}
                            onClick={this.onSearchButtonClicked}
                            ariaLabel={loc(SearchPlaceholder)}
                        />
                    )}
                </div>
                {/* Anchor div required for search filters. */}
                <div id={SEARCHBOX_ANCHOR_ID} />
            </div>
        );
    }

    /**
     * Sets focus on SearchInput component.
     */
    public focusSearchInput = () => {
        if (this.searchInput.current) {
            onSearchBoxFocused(this.props.scenarioId);
            this.searchInput.current.setFocus();
        }
    };

    /**
     * Click handler for the search button.
     */
    private onSearchButtonClicked = async (evt: React.MouseEvent<unknown>) => {
        evt.stopPropagation();
        const { scenarioId } = this.props;

        /**
         * If search box isn't empty, take action depending on whether
         * or not the user is in command mode. Otherwise, just focus the
         * search input.
         */
        const isSearchBoxEmpty = await lazyIsSearchBoxEmpty.import();
        const input = this.searchInput.current;
        if (input && !isSearchBoxEmpty(scenarioId)) {
            startSearch('SearchButton', scenarioId, true /* explicitSearch */);
        } else {
            this.focusSearchInput();
        }
    };

    /**
     * Click handler to set focus on the SearchInput component.
     */
    private onSearchBoxClick = (evt: React.MouseEvent<unknown>) => {
        evt.stopPropagation();
        this.focusSearchInput();
    };

    /**
     * Dispatches the exitSearch action to notify consumer that user wants to exit
     * the search experience.
     */
    private onExit = (actionSource: string) => {
        exitSearch(actionSource, this.props.scenarioId);
    };

    /**
     * Focus handler for when an element within the SearchBox component loses focus.
     * We check if the element receiving focus is within the SearchBox component,
     * and only dispatch the blur action if it is not.
     */
    private onSearchBoxBlur = (evt: React.FocusEvent<EventTarget>) => {
        let relatedTargetElement = evt.relatedTarget as HTMLElement;

        // Fallback for IE, which doesn't always set relatedTarget.
        if (!relatedTargetElement) {
            relatedTargetElement = document.activeElement as HTMLElement;
        }

        /**
         * A way of determining if element that caused JavaScript event is from
         * within search box.
         *
         * In case of IE, as relatedTarget is undefined we fallback to
         * activeElement.
         *
         * When user clicks on the persona card, the activeElement is the
         * LPC card's container div whose id is 'LPCPseudoTabbableElement'. The elementContains
         * function fails for this element and hence we are falling back to comparing the ID of this
         * div when determining whether the search box entirely lost focus.
         * VSO: 32596 - Search input grabs focus upon dismissing the suggestion dropdown while the
         * LPC card was open for any people suggestion
         *
         * For Safari, the relatedTargetElement is the div that the target is
         * contained within, so in the case of the search box interactions
         * execute search button, the elementContains check fails, so just check
         * if that element IS the search box container itself.
         */
        const isTargetElementWithinSearchBox =
            elementContains(this.searchBoxContainer, relatedTargetElement) ||
            relatedTargetElement.id == LPC_CARD_ID ||
            (relatedTargetElement.firstElementChild &&
                relatedTargetElement.firstElementChild.id === SEARCHBOX_ID);

        // Truly "blur" only if event is from outside of search box.
        if (!isTargetElementWithinSearchBox) {
            // setIsSuggestionsDropdownVisible(false, this.props.scenarioId);
            onSearchBoxBlurred(this.props.scenarioId);
        }
    };

    /**
     * Click handler when user chooses to click the "X" on a pill to remove it
     * from the search box.
     */
    private onRemovePill = async (suggestionPillId: string) => {
        onRemoveSuggestionPill(suggestionPillId, this.props.scenarioId, 'Click');
    };

    /**
     * Click handler when user chooses to click the "X" on a segment (represented
     * as a pill) to remove it from the search box.
     */
    private onRemoveSegment = async (segmentKey: string) => {
        onRemoveGuidedFormulationPill(segmentKey, this.props.scenarioId);
    };
}
